import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
    Footer,
    Header,
    Meta,
    StickyCTAButtons
} from '$partials/global';

import {
    ModuleDispatcher,
    ModulePrimaryContentCarouselBlock
} from '$partials/modules/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */

const JobDetailPage = (props) => {
    const page = props.data.page;
    const newsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');
    const firstAccordion = page?.modules?.find((item) => item.__typename === 'AccordionBlock');

    // state
    const [selectedAccordionId, setSelectedAccordionId] = useState(firstAccordion?.id);

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                pageType={page?.__typename}
                standfirst={page?.standfirst} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main id='main-content' >
                <section className='layout-container pb-100'>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='md:w-7/12 layout-grid-col-padding'>
                            <p className='type-style-4 pb-30'>
                                Job
                            </p>

                            {page?.title && (
                                <h1 className='type-style-2 pb-30 md:pb-35'>
                                    {page.title}
                                </h1>
                            )}

                            {page?.authority && (
                                <p className='hidden md:block type-style-4 pb-30'>
                                    sought by {page.authority.title}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className='layout-grid-negative-gutter pb-40 sm:pb-30 md:pb-60'>
                        <dl className='flex flex-wrap md:flex-nowrap event-info'>
                            <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                            {page?.jobLocation && (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Location
                                    </dt>

                                    <dd className='type-style-4'>
                                        {page.jobLocation}
                                    </dd>
                                </div>
                            )}

                            {page?.contractType && (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Contract
                                    </dt>

                                    <dd className='type-style-4'>
                                        {page?.contractType?.title}
                                    </dd>
                                </div>
                            )}

                            {page?.salaryMin === page?.salaryMax ? (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Salary
                                    </dt>

                                    <dd className='type-style-4'>
                                        {page.salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(page.salaryMin)}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            ) : (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Salary
                                    </dt>

                                    <dd className='type-style-4'>
                                        {page.salaryMin && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(page.salaryMin)}
                                            </span>
                                        )}

                                        {page.salaryMin && page.salaryMax && (
                                            <span> &ndash; </span>
                                        )}

                                        {page.salaryMax && (
                                            <span>
                                                {new Intl.NumberFormat(
                                                    'en-GB',
                                                    {
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0,
                                                        minimumFractionDigits: 0,
                                                        style: 'currency',
                                                    }
                                                ).format(page.salaryMax)}
                                            </span>
                                        )}
                                    </dd>
                                </div>
                            )}

                            {page?.duration && (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Duration
                                    </dt>

                                    <dd className='type-style-4'>
                                        {page?.duration?.title}
                                    </dd>
                                </div>
                            )}

                            {page?.applicationClosingDate && (
                                <div className='sm:w-4/12 md:w-2/12 layout-grid-col-padding'>
                                    <dt className='type-style-4 text-charcoal pb-10'>
                                        Application Close
                                    </dt>

                                    <dd className='type-style-4'>
                                        {dayjs(page.applicationClosingDate).format('D MMM YYYY')}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </section>

                {/* modules */}
                <div className='bg-white relative'>
                    {page?.modules?.map((module, idx) => {
                        if (module.__typename === 'AccordionBlock') {
                            module.isSelectedAccordion = (module.id === selectedAccordionId);
                            module.setIsSelectedAccordion = setSelectedAccordionId;
                        }

                        if (module.__typename === 'NewsletterBlock' || module.__typename === 'CalendarBlock') {
                            module.borderTop = true;
                        }

                        return (
                            <ModuleDispatcher
                                index={idx}
                                isDetailPage={true}
                                key={idx}
                                {...module} />
                        );
                    })}
                </div>

                {/* related jobs */}
                {page?.relatedJobs?.edges?.length > 0 && (
                    <div className='relative bg-white'>
                        <div className='border-t border-t-charcoal' />
                        <ModulePrimaryContentCarouselBlock
                            heading='Our latest jobs'
                            pages={page?.relatedJobs?.edges.map(itm => {
                                return { page: itm.node };
                            })} />
                    </div>
                )}
            </main>

            <Footer
                hasNewsletterModule={newsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
        page: PropTypes.object
    }),
    domain: PropTypes.string,
};

JobDetailPage.propTypes = propTypes;

export default JobDetailPage;
